// src/pages/qc/DailyAnalysisModal.jsx
import { useState } from "react";
import { todayDubai } from "../../utils/dateUtils";

const EMPTY = {
  date: todayDubai(),
  productId: "",
  batchNo: "",
  viscosity: "",
  viscTemp: "",
  viscUnit: "CP",
  ph: "",
  phTemp: "",
  density: "",
  densityTemp: "",
  solidPct: "",
  remarks: "",
};

function num(v) { return v === "" ? null : parseFloat(v); }

function Row({ label, children }) {
  return (
    <div className="form-row">
      <span className="form-label">{label}</span>
      <div style={{ display:"flex", gap:6, alignItems:"center", flex:1 }}>{children}</div>
    </div>
  );
}

function NumInput({ value, onChange, placeholder, style }) {
  return (
    <input type="number" className="form-input" value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder} step="any"
      style={{ width:90, ...style }} />
  );
}

export function DailyAnalysisModal({ products, onSave, onCancel }) {
  const [form,   setForm]   = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.productId)       { setError("Product is required.");       return; }
    if (!form.batchNo.trim())  { setError("Batch No is required.");      return; }
    if (form.viscosity === "") { setError("Viscosity is required.");     return; }
    if (form.ph === "")        { setError("pH is required.");            return; }
    if (form.solidPct === "")  { setError("Solid Content is required."); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        date:      form.date,
        productId: form.productId,
        batchNo:   form.batchNo.trim(),
        viscosity: num(form.viscosity),
        viscTemp:  num(form.viscTemp),
        viscUnit:  form.viscUnit || null,
        ph:          num(form.ph),
        phTemp:      num(form.phTemp),
        density:     num(form.density),
        densityTemp: num(form.densityTemp),
        solidPct:    num(form.solidPct),
        remarks:   form.remarks.trim() || null,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  const sortedProducts = [...products].sort((a, b) => a.id.localeCompare(b.id));

  return (
    <div className="modal wide">
      <div className="modal-header">
        <h3 className="modal-title">Add Daily Analysis</h3>
        <button className="btn" onClick={onCancel}>✕</button>
      </div>

      <form onSubmit={handleSubmit}>
        <Row label="Production Date">
          <input type="date" className="form-input" value={form.date}
            onChange={e => set("date", e.target.value)} required style={{ width:160 }} />
        </Row>

        <Row label="Product *">
          <select className="form-select" value={form.productId}
            onChange={e => set("productId", e.target.value)} style={{ flex:1 }}>
            <option value="">— select product —</option>
            {sortedProducts.map(p => (
              <option key={p.id} value={p.id}>{p.id} — {p.name}</option>
            ))}
          </select>
        </Row>

        <Row label="Batch No *">
          <input className="form-input" placeholder="e.g. B-2501" value={form.batchNo}
            onChange={e => set("batchNo", e.target.value)} style={{ flex:1 }} />
        </Row>

        <div className="form-sep" />

        <Row label="Viscosity *">
          <NumInput value={form.viscosity} onChange={v => set("viscosity", v)} placeholder="value" />
          <select className="form-select" value={form.viscUnit}
            onChange={e => set("viscUnit", e.target.value)} style={{ width:70 }}>
            <option value="CP">CP</option>
            <option value="sec">sec</option>
          </select>
          <span style={{ fontSize:12, color:"#888" }}>@</span>
          <NumInput value={form.viscTemp} onChange={v => set("viscTemp", v)} placeholder="°C" />
          <span style={{ fontSize:12, color:"#888" }}>°C</span>
        </Row>

        <Row label="pH *">
          <NumInput value={form.ph} onChange={v => set("ph", v)} placeholder="e.g. 7.5" />
          <span style={{ fontSize:12, color:"#888" }}>@</span>
          <NumInput value={form.phTemp} onChange={v => set("phTemp", v)} placeholder="°C" />
          <span style={{ fontSize:12, color:"#888" }}>°C</span>
        </Row>

        <Row label="Density (g/mL)">
          <NumInput value={form.density} onChange={v => set("density", v)} placeholder="e.g. 1.050" />
          <span style={{ fontSize:12, color:"#888" }}>@</span>
          <NumInput value={form.densityTemp} onChange={v => set("densityTemp", v)} placeholder="°C" />
          <span style={{ fontSize:12, color:"#888" }}>°C</span>
        </Row>

        <Row label="Solid Content (%) *">
          <NumInput value={form.solidPct} onChange={v => set("solidPct", v)} placeholder="e.g. 52.3" />
        </Row>

        <div className="form-sep" />

        <Row label="Remarks">
          <textarea className="form-input" rows={2} placeholder="Optional notes…" value={form.remarks}
            onChange={e => set("remarks", e.target.value)}
            style={{ flex:1, resize:"vertical" }} />
        </Row>

        {error && <div className="alert-warn" style={{ marginTop:8 }}>{error}</div>}

        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
          <button type="button" className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
